import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Move through a list without touching the mouse.
 *
 * This is a tool somebody works a queue in every morning, and every action
 * in it was a click. `j`/`k` move, `Enter` opens, `g`/`G` jump to the ends,
 * Escape lets go. Arrow keys do the same, because not everybody has met
 * vim and the cost of supporting both is two lines.
 *
 * Two rules it keeps:
 *
 * Typing wins. A key pressed while the caret is in a field is text, not a
 * command, so a search box does not become unusable the moment a list is
 * on screen.
 *
 * Nothing is selected until somebody asks. The cursor starts absent, so a
 * page does not open highlighted at a row nobody chose — the highlight is
 * a claim that this row is where you are, and it should be true.
 */
export interface ListKeys {
  /** The row the cursor is on, or null before anybody has moved. */
  cursor: number | null;
  setCursor: (index: number | null) => void;
  /** Props for each row: marks the active one and keeps it in view. */
  rowProps: (index: number) => {
    "data-cursor": boolean | undefined;
    ref: (el: HTMLElement | null) => void;
  };
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
    || el.isContentEditable;
}

export function useKeyboardList(
  count: number,
  onOpen?: (index: number) => void,
  enabled = true,
): ListKeys {
  const [cursor, setCursor] = useState<number | null>(null);
  const rows = useRef<(HTMLElement | null)[]>([]);

  // A shorter list must not leave the cursor pointing past the end.
  useEffect(() => {
    setCursor((c) => (c === null ? null : Math.min(c, count - 1)));
  }, [count]);

  useEffect(() => {
    if (!enabled || count === 0) return;

    function onKey(e: KeyboardEvent) {
      if (isTyping(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;

      const move = (next: number) => {
        e.preventDefault();
        const index = Math.max(0, Math.min(count - 1, next));
        setCursor(index);
        // Optional: a row element that cannot scroll must not take the
        // whole handler down with it, which would stop navigation dead.
        rows.current[index]?.scrollIntoView?.({ block: "nearest" });
      };

      switch (e.key) {
        case "j": case "ArrowDown":
          return move(cursor === null ? 0 : cursor + 1);
        case "k": case "ArrowUp":
          return move(cursor === null ? 0 : cursor - 1);
        case "g":
          return move(0);
        case "G":
          return move(count - 1);
        case "Enter":
          if (cursor !== null && onOpen) { e.preventDefault(); onOpen(cursor); }
          return;
        case "Escape":
          return setCursor(null);
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, cursor, onOpen, enabled]);

  const rowProps = useCallback((index: number) => ({
    "data-cursor": cursor === index ? true : undefined,
    ref: (el: HTMLElement | null) => { rows.current[index] = el; },
  }), [cursor]);

  return { cursor, setCursor, rowProps };
}
